import type { GridProps } from '@mui/material/Grid'
import Grid from '@mui/material/Grid'
import type { ReactNode } from 'react'

const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl'] as const

type Breakpoint = (typeof BREAKPOINTS)[number]

export interface RaGridProps extends Omit<GridProps, 'size'> {
  children?: ReactNode
  /** Accepted and ignored: MUI 9 has no `item` — every Grid child is one. */
  item?: boolean
  size?: GridProps['size']
  xs?: number | 'auto' | boolean
  sm?: number | 'auto' | boolean
  md?: number | 'auto' | boolean
  lg?: number | 'auto' | boolean
  xl?: number | 'auto' | boolean
}

/**
 * A Grid that still speaks the pre-v6 MUI layout API.
 *
 * ra-compact-ui's RaGrid existed to strip the props react-admin 3 injected into
 * layout components (`basePath`, `record`, `resource`, `version`) before they
 * reached MUI Grid and landed on the DOM. react-admin 5 injects nothing, so
 * that job is gone — but MUI 9's Grid dropped `item` and the per-breakpoint
 * props in favour of `size={{ md: 4 }}`, and the call sites compute breakpoints
 * arithmetically. Translating here keeps that math in one place.
 */
export const RaGrid = ({ item, xs, sm, md, lg, xl, size, children, ...rest }: RaGridProps) => {
  const legacy: Partial<Record<Breakpoint, unknown>> = { xs, sm, md, lg, xl }
  const derived = BREAKPOINTS.reduce<Record<string, unknown>>(
    (acc, bp) => (legacy[bp] === undefined ? acc : { ...acc, [bp]: legacy[bp] }),
    {}
  )
  const resolved = size ?? (Object.keys(derived).length > 0 ? derived : undefined)

  return (
    <Grid {...rest} {...(resolved ? { size: resolved as GridProps['size'] } : {})}>
      {children}
    </Grid>
  )
}

export default RaGrid
